import { useEffect, useMemo, useState } from 'react';

import { FastAverageColor, FastAverageColorOptions } from 'fast-average-color';

import { RGBTuple } from './types';
import { getContrast, hexToRgb } from './utils';

const fac = new FastAverageColor();
const facOptions: FastAverageColorOptions = {
    crossOrigin: 'anonymous',
};

type Details = {
    dominantImageAColor?: RGBTuple;
    dominantImageBColor?: RGBTuple;
    imageAContrast: number;
    imageBContrast: number;
};

type BestImageAndDetails = [string | null, Details | null];

const useBestImageForBackgroundColor = (
    imageAUrl: string,
    imageBUrl: string,
    backgroundColor: string
): BestImageAndDetails => {
    const [dominantImageAColor, setDominantImageAColor] = useState<RGBTuple>();
    const [dominantImageBColor, setDominantImageBColor] = useState<RGBTuple>();

    useEffect(() => {
        const getImageAColor = async () => {
            // Using a query parameter to avoid reusing a cached non-cross-origin request. This should force a
            // cross-origin request and allow that request to be cached.
            const color = await fac.getColorAsync(`${imageAUrl}?q=1`, facOptions);
            setDominantImageAColor(hexToRgb(color.hex));
        };

        getImageAColor();
    }, [imageAUrl]);

    useEffect(() => {
        const getImageBColor = async () => {
            const color = await fac.getColorAsync(`${imageBUrl}?q=1`, facOptions);
            setDominantImageBColor(hexToRgb(color.hex));
        };

        getImageBColor();
    }, [imageBUrl]);

    const imageAContrast = useMemo<number>(() => {
        const rgbBackgroundColor = hexToRgb(backgroundColor);

        if (dominantImageAColor && rgbBackgroundColor) {
            return getContrast(dominantImageAColor, rgbBackgroundColor);
        }

        return 0;
    }, [dominantImageAColor, backgroundColor]);

    const imageBContrast = useMemo(() => {
        const rgbBackgroundColor = hexToRgb(backgroundColor);

        if (dominantImageBColor && rgbBackgroundColor) {
            return getContrast(dominantImageBColor, rgbBackgroundColor);
        }

        return 0;
    }, [dominantImageBColor, backgroundColor]);

    if (dominantImageAColor === undefined && dominantImageBColor === undefined) {
        return [null, null];
    }

    return [
        imageAContrast > imageBContrast ? imageAUrl : imageBUrl,
        {
            dominantImageAColor,
            dominantImageBColor,
            imageAContrast,
            imageBContrast,
        },
    ];
};

export default useBestImageForBackgroundColor;
